import { motion } from "framer-motion";
import { LOCATION_LEVELS } from "../lib/materials";
import { fadeUp, viewportOnce, stagger, EASE } from "../lib/motion";

const LevelDiagram = () => (
    <svg
        viewBox="0 0 400 400"
        fill="none"
        className="w-full max-w-md"
        aria-hidden="true"
    >
        {[
            { s: 24, c: "rgba(255,255,255,0.22)", w: 1 },
            { s: 76, c: "rgba(255,255,255,0.26)", w: 1 },
            { s: 128, c: "rgba(255,255,255,0.32)", w: 1 },
            { s: 180, c: "rgba(255,255,255,0.4)", w: 1 },
        ].map((r, i) => (
            <motion.rect
                key={r.s}
                x={r.s}
                y={r.s}
                width={400 - r.s * 2}
                height={400 - r.s * 2}
                stroke={r.c}
                strokeWidth={r.w}
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={viewportOnce}
                transition={{ duration: 1.4, ease: EASE, delay: 0.2 + i * 0.22 }}
            />
        ))}
        <motion.rect
            x={168}
            y={168}
            width={64}
            height={64}
            stroke="#FF5B00"
            strokeWidth={1.5}
            initial={{ pathLength: 0 }}
            whileInView={{ pathLength: 1 }}
            viewport={viewportOnce}
            transition={{ duration: 1.4, ease: EASE, delay: 1.2 }}
        />
        <motion.path
            d="M200 156 V244 M156 200 H244"
            stroke="#FF5B00"
            strokeWidth={1}
            initial={{ pathLength: 0, opacity: 0 }}
            whileInView={{ pathLength: 1, opacity: 1 }}
            viewport={viewportOnce}
            transition={{ duration: 1, ease: EASE, delay: 1.6 }}
        />
        <motion.text
            x={200}
            y={258}
            textAnchor="middle"
            fill="#94A3B8"
            fontSize="9"
            fontFamily="'JetBrains Mono', monospace"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={viewportOnce}
            transition={{ duration: 0.8, delay: 2 }}
        >
            SITE
        </motion.text>
    </svg>
);

const LocationHierarchy = () => (
    <section
        id="hierarchy"
        data-testid="location-hierarchy"
        className="relative bg-ink py-24 lg:py-36"
    >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
            <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={viewportOnce}
                className="flex items-center gap-6"
            >
                <p className="font-mono text-[13px] tracking-[0.3em] text-mist">
                    LOCATION-FIRST
                </p>
                <span className="h-px flex-1 bg-white/10" />
                <p className="font-mono text-[13px] tracking-[0.3em] text-cad">
                    L.01 — L.05
                </p>
            </motion.div>

            <div className="mt-14 grid items-center gap-16 lg:grid-cols-12">
                <div className="lg:col-span-6">
                    <motion.h2
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        data-testid="location-headline"
                        className="font-display text-4xl font-semibold leading-[1.1] text-white md:text-6xl"
                    >
                        WHERE YOU BUILD
                        <span className="block">
                            MATTERS<span className="text-accent">.</span>
                        </span>
                    </motion.h2>
                    <motion.p
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="mt-6 max-w-md text-[16px] leading-relaxed text-mist"
                    >
                        BuildEase prioritises relevant local pricing, organised
                        outward from the supplier closest to your site.
                    </motion.p>

                    <motion.div
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={viewportOnce}
                        className="mt-12"
                    >
                        {LOCATION_LEVELS.map((l) => (
                            <motion.div
                                key={l.n}
                                variants={fadeUp}
                                data-testid={`location-level-${l.n
                                    .toLowerCase()
                                    .replace(".", "-")}`}
                                className="flex items-center justify-between border-t border-white/10 py-4 last:border-b"
                            >
                                <span className="flex items-center gap-4">
                                    <span
                                        className={`font-mono text-[13px] ${
                                            l.tag ? "text-accent" : "text-cad"
                                        }`}
                                    >
                                        {l.n}
                                    </span>
                                    <span className="font-display text-[16px] font-medium tracking-wide text-white">
                                        {l.label}
                                    </span>
                                </span>
                                {l.tag && (
                                    <span className="border border-accent/60 px-2.5 py-1 font-mono text-[13px] tracking-[0.22em] text-accent">
                                        {l.tag}
                                    </span>
                                )}
                            </motion.div>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="relative lg:col-span-6 lg:pl-10"
                >
                    <div className="blueprint-bg blueprint-fade pointer-events-none absolute inset-0 opacity-70" />
                    <div className="relative flex justify-center py-6">
                        <LevelDiagram />
                    </div>
                    <div className="relative mt-2 flex items-center justify-between font-mono text-[13px] tracking-[0.2em] text-cad">
                        <span>FIG. L.01 — PRICING PROXIMITY</span>
                        <span>29.8587° S / 31.0218° E</span>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

export default LocationHierarchy;
